import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { requestKnownSwimResearch } from './knownSwimResearch';

const KnownSwimResearchContext = createContext(null);

export const KnownSwimResearchProvider = ({ children }) => {
  const pendingRequestsRef = useRef(new Map());
  const [researchingIds, setResearchingIds] = useState(new Set());
  const [researchResults, setResearchResults] = useState(new Map());
  const [researchErrors, setResearchErrors] = useState(new Map());

  const researchSwim = useCallback((knownSwimId, refresh = false) => {
    const existingRequest = pendingRequestsRef.current.get(knownSwimId);
    if (existingRequest) return existingRequest;

    setResearchingIds((current) => new Set(current).add(knownSwimId));
    setResearchErrors((current) => {
      const updated = new Map(current);
      updated.delete(knownSwimId);
      return updated;
    });
    const request = requestKnownSwimResearch(knownSwimId, refresh)
      .then((result) => {
        setResearchResults((current) => new Map(current).set(knownSwimId, result));
        return result;
      })
      .catch((error) => {
        setResearchErrors((current) => new Map(current).set(
          knownSwimId,
          error.message || 'Unable to research this swim.'
        ));
        throw error;
      })
      .finally(() => {
        pendingRequestsRef.current.delete(knownSwimId);
        setResearchingIds((current) => {
          const updated = new Set(current);
          updated.delete(knownSwimId);
          return updated;
        });
      });

    pendingRequestsRef.current.set(knownSwimId, request);
    return request;
  }, []);

  return (
    <KnownSwimResearchContext.Provider value={{
      researchSwim,
      isResearching: (knownSwimId) => researchingIds.has(knownSwimId),
      researchResults,
      researchResult: (knownSwimId) => researchResults.get(knownSwimId),
      researchError: (knownSwimId) => researchErrors.get(knownSwimId)
    }}>
      {children}
    </KnownSwimResearchContext.Provider>
  );
};

export const useKnownSwimResearch = () => {
  const context = useContext(KnownSwimResearchContext);
  if (!context) throw new Error('useKnownSwimResearch must be used within KnownSwimResearchProvider.');
  return context;
};
